import React, { useState, useEffect } from "react";
import axios from "axios";

const POReport = () => {
  const [vendors, setVendors] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kitchenId, setKitchenId] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Date filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [oldStartDate, setOldStartDate] = useState("");
  const [oldEndDate, setOldEndDate] = useState("");

  // Helper function to format date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Function to calculate previous period dates based on current period
  const calculatePreviousPeriod = (currentStart, currentEnd) => {
    if (!currentStart || !currentEnd) return { oldStart: "", oldEnd: "" };
    
    const startDateObj = new Date(currentStart);
    const endDateObj = new Date(currentEnd);
    
    // Calculate the duration of current period in days
    const periodDuration = Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));
    
    // Calculate previous period dates
    const oldStartDateObj = new Date(startDateObj);
    oldStartDateObj.setDate(startDateObj.getDate() - periodDuration - 1); // Subtract period duration + 1 day to get previous period
    
    const oldEndDateObj = new Date(oldStartDateObj);
    oldEndDateObj.setDate(oldStartDateObj.getDate() + periodDuration);
    
    return {
      oldStart: formatDate(oldStartDateObj),
      oldEnd: formatDate(oldEndDateObj)
    };
  };

  // Initialize with current month dates
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setStartDate(formatDate(firstDay));
    setEndDate(formatDate(lastDay));
  }, []);

  // Auto-calculate previous period when current period dates change
  useEffect(() => {
    if (startDate && endDate) {
      const { oldStart, oldEnd } = calculatePreviousPeriod(startDate, endDate);
      setOldStartDate(oldStart);
      setOldEndDate(oldEnd);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.kitchen) {
      setKitchenId(user.kitchen);
    }
  }, []);

  // Fetch data when kitchenId or dates change
  useEffect(() => {
    if (kitchenId && startDate && endDate && oldStartDate && oldEndDate) {
      fetchDataList(kitchenId, startDate, endDate, oldStartDate, oldEndDate);
    }
  }, [kitchenId, startDate, endDate, oldStartDate, oldEndDate]);

  // Fetch vendors with date filters
  const fetchDataList = async (kitchenId, startDate, endDate, oldStartDate, oldEndDate) => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://api-ims.indevconsultancy.in/purches-report.php",
        {
          params: {
            kitchen_id: kitchenId,
            start_date: startDate,
            end_date: endDate,
            old_start_date: oldStartDate,
            old_end_date: oldEndDate
          }
        }
      );
      
      if (response.data && response.data.success) {
        setVendors(response.data.data || []);
        setFilteredData(response.data.data || []);
      } else {
        setVendors([]);
        setFilteredData([]);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setLoading(false);
      setVendors([]);
      setFilteredData([]);
    }
  };

  const handleExport = async () => {
    if (!kitchenId) {
      alert("Kitchen ID not available");
      return;
    }
    
    if (!startDate || !endDate || !oldStartDate || !oldEndDate) {
      alert("Please select all date ranges");
      return;
    }
    
    setExporting(true);
    try {
      // Build the export URL with parameters
      const exportUrl = `https://api-ims.indevconsultancy.in/report/purches-report.php?kitchen_id=${kitchenId}&start_date=${startDate}&end_date=${endDate}&old_start_date=${oldStartDate}&old_end_date=${oldEndDate}`;
      
      // Trigger download
      const link = document.createElement("a");
      link.href = exportUrl;
      link.download = `report_${kitchenId}.xlsx`; // or .csv depending on your API
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export report. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // Handle date filter changes
  const handleFilter = () => {
    if (kitchenId && startDate && endDate && oldStartDate && oldEndDate) {
      fetchDataList(kitchenId, startDate, endDate, oldStartDate, oldEndDate);
    } else {
      alert("Please select all date ranges");
    }
  };

  // Handle reset filters
  const handleReset = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setStartDate(formatDate(firstDay));
    setEndDate(formatDate(lastDay));
  };

  // Calculate totals
  const calculateTotals = () => {
    const totals = {
      recievedquantity: 0,
      expenditure_quantity: 0,
      opening_quantity: 0,
      closing_quantity: 0,
      closing_value: 0
    };

    filteredData.forEach(item => {
      totals.recievedquantity += parseFloat(item.recievedquantity) || 0;
      totals.expenditure_quantity += parseFloat(item.expenditure_quantity) || 0;
      totals.opening_quantity += parseFloat(item.opening_quantity) || 0;
      totals.closing_quantity += parseFloat(item.closing_quantity) || 0;
      totals.closing_value += parseFloat(item.closing_value) || 0;
    });

    return totals;
  };

  const totals = calculateTotals();

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Purchase Order Report</h3>
                <div className="card-tools">
                  <button 
                    className="btn btn-sm btn-success mr-2"
                    onClick={handleExport}
                    disabled={exporting || loading}
                  >
                    {exporting ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-download mr-1"></i>
                        Export Report
                      </>
                    )}
                  </button>
                  <button 
                    className="btn btn-sm btn-primary mr-2" 
                    onClick={handleFilter}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                        Loading...
                      </>
                    ) : (
                      'Apply Filters'
                    )}
                  </button>
                  <button 
                    className="btn btn-sm btn-secondary" 
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Reset
                  </button>
                </div>
              </div>
              
              <div className="card-body">
                {/* Date Filters - Only Current Period Visible */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Current Period Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Current Period End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  {/* Hidden Previous Period Fields - Auto-calculated */}
                  <input type="hidden" value={oldStartDate} />
                  <input type="hidden" value={oldEndDate} />
                  
                  {/* Optional: Display calculated previous period for reference */}
                  <div className="col-md-6">
                    <div className="alert alert-info" style={{ padding: '5px 10px', marginTop: '30px' }}>
                      <small>
                        <strong>Previous Period:</strong> {oldStartDate} to {oldEndDate} (auto-calculated)
                      </small>
                    </div>
                  </div>
                </div>

                {/* Data Table */}
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <p className="mt-2">Loading data...</p>
                    </div>
                  ) : filteredData.length === 0 ? (
                    <div className="alert alert-warning text-center">
                      No data available for the selected filters
                    </div>
                  ) : (
                    <>
                      <table className="table table-bordered table-hover">
                        <thead className="thead-dark">
                          <tr>
                            <th>Sr. No.</th>
                            <th>Stock Name</th>
                            <th>Opening Quantity</th>
                            <th>Received Quantity</th>
                            <th>Expenditure Quantity</th>
                            <th>Closing Quantity</th>
                            <th>Closing Rate (₹)</th>
                            <th>Closing Value (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredData.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{item.stock_name}</td>
                              <td className={parseFloat(item.opening_quantity) < 0 ? 'text-danger' : ''}>
                                {parseFloat(item.opening_quantity).toFixed(2)}
                              </td>
                              <td>{parseFloat(item.recievedquantity).toFixed(2)}</td>
                              <td>{parseFloat(item.expenditure_quantity).toFixed(2)}</td>
                              <td className={parseFloat(item.closing_quantity) < 0 ? 'text-danger' : ''}>
                                {parseFloat(item.closing_quantity).toFixed(2)}
                              </td>
                              <td>{parseFloat(item.closing_rate || 0).toFixed(2)}</td>
                              <td>{parseFloat(item.closing_value || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                          {/* Totals Row */}
                          <tr className="table-primary font-weight-bold">
                            <td colSpan="2">TOTAL</td>
                            <td>{totals.opening_quantity.toFixed(2)}</td>
                            <td>{totals.recievedquantity.toFixed(2)}</td>
                            <td>{totals.expenditure_quantity.toFixed(2)}</td>
                            <td>{totals.closing_quantity.toFixed(2)}</td>
                            <td>—</td>
                            <td>{totals.closing_value.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POReport;